import {
    AccessPermissionRoles,
    AccessRoles,
    Permission,
    PermissionRoles,
    Permissions,
} from "./models/models";
import { areAccessPermissions, hasPermission } from "./assets/helpers/helpers";
import { getPermissionsFromToken } from "./assets/helpers/authToken";
import { AuthData } from "./redux/auth-action";

// Single source of truth for route and navigation access, shared by
// ProtectedRoute, the "/" landing redirect and the nav panel — a route the user
// cannot open must never be offered in the menu or used as a landing target.
//
// A rule passes on the granular permission from the JWT `permission` claim.
// allowedRoles stays as a fallback for the two areas the backend has no
// permission for (Iolo, History) and for sessions whose token predates the
// claim. Action-level gates keep calling hasPermission directly.
export type AccessRule = {
    requiredPermission?: Permission | Permission[];
    allowedRoles?: AccessPermissionRoles | AccessPermissionRoles[];
};

export type AccessSubject = {
    role: PermissionRoles | undefined;
    permissions: string[];
};

// No rule means "any authenticated user", which is what products, projects and
// transactions have always been.
export const canAccess = (
    subject: AccessSubject,
    rule?: AccessRule
): boolean => {
    if (rule === undefined) {
        return true;
    }

    if (
        rule.requiredPermission !== undefined &&
        hasPermission(subject.permissions, rule.requiredPermission)
    ) {
        return true;
    }

    return (
        rule.allowedRoles !== undefined &&
        areAccessPermissions(rule.allowedRoles, subject.role as PermissionRoles)
    );
};

// The token in localStorage is the same source ProtectedRoute authenticates
// against, so reading it keeps gating decisions independent of when the redux
// store gets initialized.
export const readAccessSubject = (): AccessSubject => {
    try {
        const auth: AuthData | null = JSON.parse(
            localStorage?.getItem("auth") as string
        );
        return {
            role: auth?.role,
            permissions: getPermissionsFromToken(auth?.token),
        };
    } catch {
        return { role: undefined, permissions: [] };
    }
};

const ADMIN_USER_ROLES = [AccessRoles.ADMIN, AccessRoles.USER];
// products and projects have always been open to every known role — keep them
// reachable for those while new roles are gated by the *.Read permission.
const ALL_KNOWN_ROLES = [
    AccessRoles.ADMIN,
    AccessRoles.USER,
    AccessRoles.SUPPORT,
];

export const ROUTE_ACCESS = {
    KEY_GENERATOR: {
        requiredPermission: Permissions.LICENSE_GENERATE,
        allowedRoles: ADMIN_USER_ROLES,
    },
    LICENSE_KEYS: {
        requiredPermission: Permissions.LICENSE_READ,
        allowedRoles: ADMIN_USER_ROLES,
    },
    PRODUCTS: {
        requiredPermission: Permissions.PRODUCT_READ,
        allowedRoles: ALL_KNOWN_ROLES,
    },
    PROJECTS: {
        requiredPermission: Permissions.PROJECT_READ,
        allowedRoles: ALL_KNOWN_ROLES,
    },
    // Iolo is a separate client area with no permission of its own.
    LICENSE_KEYS_IOLO: {
        allowedRoles: [AccessRoles.SUPPORT, AccessRoles.ADMIN],
    },
    USERS: {
        requiredPermission: Permissions.USER_MANAGE_ACCESS,
        allowedRoles: AccessRoles.ADMIN,
    },
    // No History permission exists on the backend yet.
    HISTORY: {
        allowedRoles: AccessRoles.ADMIN,
    },
} satisfies Record<string, AccessRule>;

// Landing priority for "/" — the first area the user can actually open.
const LANDING_ROUTES: Array<{ path: string; rule?: AccessRule }> = [
    { path: "license-keys/actual", rule: ROUTE_ACCESS.LICENSE_KEYS },
    { path: "key-generator", rule: ROUTE_ACCESS.KEY_GENERATOR },
    { path: "products/actual", rule: ROUTE_ACCESS.PRODUCTS },
    { path: "projects", rule: ROUTE_ACCESS.PROJECTS },
    { path: "users", rule: ROUTE_ACCESS.USERS },
];

// undefined means the user may open nothing at all — the caller must render a
// message rather than navigate, otherwise "/" and a blocked route bounce
// between each other forever.
export const resolveLandingPath = (
    subject: AccessSubject
): string | undefined => {
    if (subject.role === AccessRoles.SUPPORT) {
        return "license-keys/iolo";
    }

    return LANDING_ROUTES.find((route) => canAccess(subject, route.rule))?.path;
};
